import SEO from '../components/SEO';
import React from 'react';
import { Link } from 'react-router-dom';
import { Mic, MessageSquare, Phone, Clock, TrendingUp, Users, CheckCircle, ArrowRight, Zap, Shield, BarChart, Calendar } from 'lucide-react';
import { breadcrumbSchema, organizationSchema } from '../utils/structuredData';

const aiBdcSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': 'https://driven2win.com/services/ai-bdc#service',
  name: 'AI BDC Voice & SMS Agents for Dealerships',
  serviceType: 'AI-Powered Business Development Center',
  description: 'AI-powered voice and SMS agents that answer every inbound call, follow up every lead 24/7, and book appointments automatically — so your dealership never misses a deal.',
  provider: { '@type': 'Organization', name: 'Driven2WIN', url: 'https://driven2win.com' },
  areaServed: { '@type': 'Country', name: 'United States' },
  url: 'https://driven2win.com/services/ai-bdc',
};

const AiBdcPage = () => {
  return (
    <div style={styles.page}>
      <SEO
        title="AI BDC Voice & SMS Agents for Dealerships — 24/7"
        description="AI-powered BDC agents answer every call and follow up every lead in under 60 seconds — 24/7 for car, golf cart, motorcycle, ATV & RV dealers. No missed deals."
        keywords="AI BDC dealership, AI voice agents automotive, SMS lead follow-up dealership, automotive BDC AI, dealership appointment setting AI, AI phone agents car dealer, 24/7 lead follow-up dealership"
        canonicalUrl="https://driven2win.com/services/ai-bdc"
        structuredData={[aiBdcSchema, organizationSchema, breadcrumbSchema([
          { name: 'Home', url: 'https://driven2win.com' },
          { name: 'Services', url: 'https://driven2win.com/services' },
          { name: 'AI BDC Agents', url: 'https://driven2win.com/services/ai-bdc' },
        ])]}
      />

      <style>{`
        /* ── AI BDC Page — Mobile Responsive ── */
        .ai-hero-h1 {
          font-size: clamp(1.75rem, 7vw, 4.5rem) !important;
          letter-spacing: 1px !important;
          word-break: break-word !important;
        }
        .ai-hero-sub {
          font-size: clamp(0.9rem, 3.5vw, 1.375rem) !important;
        }

        @media (max-width: 768px) {
          .ai-section { padding: clamp(2.5rem, 6vw, 4rem) 0 !important; }
          .ai-h2 { font-size: clamp(1.4rem, 5.5vw, 3rem) !important; letter-spacing: 0.5px !important; word-break: break-word !important; }
          .ai-cta-h2 { font-size: clamp(1.4rem, 5.5vw, 3.5rem) !important; }
          .ai-cta-text { font-size: 15px !important; }
          .ai-intro-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
          .ai-channels-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
          .ai-features-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
          .ai-steps-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .ai-stat-box { padding: 28px 20px !important; }
          .ai-stat-num { font-size: 40px !important; }
          .ai-channel-card { padding: 28px 20px !important; }
          .ai-feature-item { flex-direction: row !important; gap: 12px !important; }
          .ai-btn {
            font-size: 16px !important;
            padding: 14px 20px !important;
            white-space: normal !important;
            text-align: center !important;
            line-height: 1.3 !important;
          }
          .ai-body { font-size: 15px !important; }
          .ai-badge { font-size: 11px !important; padding: 8px 14px !important; }
        }

        @media (max-width: 400px) {
          .ai-h2 { font-size: 1.3rem !important; }
          .ai-stat-num { font-size: 32px !important; }
        }

        .ai-pulse {
          display: inline-block;
          width: 12px;
          height: 12px;
          background: #4ADE80;
          border-radius: 50%;
          margin-right: 8px;
          box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7);
          animation: aiPulse 2s infinite;
        }
        @keyframes aiPulse {
          0%   { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7); }
          70%  { box-shadow: 0 0 0 10px rgba(74, 222, 128, 0); }
          100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
        }
      `}</style>

      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroOverlay} />
        <div style={styles.container}>
          <div style={styles.heroBadge} className="ai-badge">
            <span className="ai-pulse" />
            ALWAYS ON — 24 HOURS / 7 DAYS / 365 DAYS
          </div>
          <h1 style={styles.heroH1} className="ai-hero-h1">
            AI BDC VOICE & SMS <span style={{ color: '#FFC300' }}>AGENTS</span>
          </h1>
          <p style={styles.heroSubheadline} className="ai-hero-sub">
            Your dealership's tireless sales team — AI agents that answer every call, follow up every lead, and book appointments automatically. <strong>24/7. No days off. No missed deals.</strong>
          </p>
          <div style={styles.heroStats}>
            <div style={styles.heroStat}>
              <div style={styles.heroStatNum}>100%</div>
              <div style={styles.heroStatLabel}>Calls Answered</div>
            </div>
            <div style={styles.heroStatDivider} />
            <div style={styles.heroStat}>
              <div style={styles.heroStatNum}>&lt; 60s</div>
              <div style={styles.heroStatLabel}>Lead Response Time</div>
            </div>
            <div style={styles.heroStatDivider} />
            <div style={styles.heroStat}>
              <div style={styles.heroStatNum}>24/7</div>
              <div style={styles.heroStatLabel}>Always Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section style={styles.problemSection} className="ai-section">
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="ai-h2">
            THE PROBLEM EVERY DEALER <span style={{ color: '#E31E24' }}>KNOWS</span>
          </h2>
          <div style={styles.introGrid} className="ai-intro-grid">
            <div style={styles.problemBox}>
              <h3 style={styles.problemTitle}>Without AI BDC</h3>
              {[
                '30–50% of inbound calls go unanswered',
                'Leads wait hours or days for follow-up',
                'Sales team too busy to handle every inquiry',
                'Nights, weekends & holidays = zero coverage',
                'Missed calls = missed deals — $10K+ per lost unit',
              ].map((item, i) => (
                <div key={i} style={styles.problemItem}>
                  <span style={styles.xIcon}>✕</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div style={styles.solutionBox}>
              <h3 style={styles.solutionTitle}>With Driven2WIN AI BDC</h3>
              {[
                'Every call answered instantly — zero hold time',
                'Every lead followed up in under 60 seconds',
                'Sales team focuses on closing, not chasing',
                'Full coverage nights, weekends & holidays',
                'More appointments = more units sold = more gross',
              ].map((item, i) => (
                <div key={i} style={styles.solutionItem}>
                  <CheckCircle size={20} color="#4ADE80" style={{ flexShrink: 0 }} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Channels */}
      <section style={{ ...styles.channelsSection }} className="ai-section">
        <div style={styles.container}>
          <h2 style={{ ...styles.sectionH2, color: '#FFFFFF', textAlign: 'center' }} className="ai-h2">
            TWO POWERFUL <span style={{ color: '#FFC300' }}>CHANNELS</span>
          </h2>
          <p style={styles.channelsSubhead}>
            Our AI agents work across voice and text simultaneously — meeting every customer where they are.
          </p>
          <div style={styles.channelsGrid} className="ai-channels-grid">

            <div style={styles.channelCard} className="ai-channel-card">
              <div style={{ ...styles.channelIconWrap, background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)' }}>
                <Mic size={48} color="#FFFFFF" />
              </div>
              <h3 style={styles.channelTitle}>AI VOICE AGENT</h3>
              <p style={styles.channelDesc}>
                A natural-sounding AI answers every inbound call in under 2 rings — introducing your dealership, qualifying the caller, handling FAQs, and booking appointments directly into your calendar.
              </p>
              <div style={styles.channelFeatures}>
                {[
                  'Natural conversation — sounds like a real person',
                  'Qualifies leads: budget, timeline, vehicle interest',
                  'Books appointments live during the call',
                  'Transfers hot leads to available salespeople',
                  'Full call recording and transcription',
                  'Handles after-hours and overflow calls',
                ].map((f, i) => (
                  <div key={i} style={styles.channelFeatureItem}>
                    <CheckCircle size={16} color="#FFC300" style={{ flexShrink: 0 }} />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.channelCard} className="ai-channel-card">
              <div style={{ ...styles.channelIconWrap, background: 'linear-gradient(135deg, #0080FF 0%, #0055CC 100%)' }}>
                <MessageSquare size={48} color="#FFFFFF" />
              </div>
              <h3 style={styles.channelTitle}>AI SMS AGENT</h3>
              <p style={styles.channelDesc}>
                The moment a lead comes in — from your website, Facebook, or event — the AI SMS agent fires a personalized text within 60 seconds, initiates a conversation, and books the appointment before your competition even calls back.
              </p>
              <div style={styles.channelFeatures}>
                {[
                  'Instant response — under 60 seconds, always',
                  'Personalized to vehicle type and inquiry',
                  'Two-way conversation — answers questions naturally',
                  'Appointment booking via text confirmation',
                  'Re-engagement sequences for no-show leads',
                  'Integrates with your CRM automatically',
                ].map((f, i) => (
                  <div key={i} style={styles.channelFeatureItem}>
                    <CheckCircle size={16} color="#FFC300" style={{ flexShrink: 0 }} />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={styles.howSection} className="ai-section">
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="ai-h2">
            HOW IT <span style={{ color: '#E31E24' }}>WORKS</span>
          </h2>
          <div style={styles.stepsGrid} className="ai-steps-grid">
            {[
              {
                num: '1',
                icon: <Phone />,
                title: 'Lead or Call Comes In',
                desc: 'A customer calls your dealership, submits a web form, responds to a Facebook ad, or texts in. The AI activates instantly.',
              },
              {
                num: '2',
                icon: <Zap />,
                title: 'AI Engages Immediately',
                desc: 'Within seconds — not hours — the AI voice or SMS agent greets the customer personally and starts a natural conversation.',
              },
              {
                num: '3',
                icon: <Calendar />,
                title: 'Appointment Booked',
                desc: 'The AI qualifies the lead, answers questions, and books a confirmed appointment directly into your calendar — no human involvement needed.',
              },
              {
                num: '4',
                icon: <Users />,
                title: 'Your Team Closes the Deal',
                desc: 'Your salespeople receive a full lead summary before the appointment — budget, vehicle interest, timeline, and conversation history.',
              },
            ].map((step, i) => (
              <div key={i} style={styles.stepCard}>
                <div style={styles.stepNumber}>{step.num}</div>
                <div style={styles.stepIconWrap}>
                  {React.cloneElement(step.icon, { size: 36, color: '#E31E24' })}
                </div>
                <h3 style={styles.stepTitle}>{step.title}</h3>
                <p style={styles.stepDesc}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results / Stats */}
      <section style={styles.statsSection} className="ai-section">
        <div style={styles.container}>
          <h2 style={{ ...styles.sectionH2, color: '#FFFFFF', textAlign: 'center' }} className="ai-h2">
            WHAT DEALERS SEE WITH <span style={{ color: '#FFC300' }}>AI BDC</span>
          </h2>
          <div style={styles.statsGrid}>
            {[
              { num: '3×', label: 'More appointments set vs. human-only BDC' },
              { num: '47%', label: 'Increase in lead-to-appointment conversion' },
              { num: '$0', label: 'Cost per after-hours call answered' },
              { num: '100%', label: 'Lead follow-up rate — no lead left behind' },
            ].map((s, i) => (
              <div key={i} style={styles.statCard} className="ai-stat-box">
                <div style={styles.statNum} className="ai-stat-num">{s.num}</div>
                <div style={styles.statLabel}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={styles.featuresSection} className="ai-section">
        <div style={styles.container}>
          <h2 style={styles.sectionH2} className="ai-h2">
            EVERYTHING INCLUDED <span style={{ color: '#E31E24' }}>OUT OF THE BOX</span>
          </h2>
          <div style={styles.featuresGrid} className="ai-features-grid">
            {[
              { icon: <Clock />, title: '24/7 Coverage', desc: 'Never miss a call or lead — nights, weekends, holidays, during events, always.' },
              { icon: <BarChart />, title: 'Live Dashboard', desc: 'See every call, text, lead, and booked appointment in real time from any device.' },
              { icon: <Shield />, title: 'CRM Integration', desc: 'Pushes every lead, note, and appointment directly into your existing CRM automatically.' },
              { icon: <TrendingUp />, title: 'Lead Re-engagement', desc: 'Automated follow-up sequences that re-engage old leads and no-shows until they buy or opt out.' },
              { icon: <Zap />, title: 'Event Mode', desc: 'During your Driven2WIN event, the AI handles the surge of inbound calls and texts — zero missed opportunities.' },
              { icon: <MessageSquare />, title: 'Multi-Vehicle Ready', desc: 'Configured for car & truck, golf cart, motorcycle, ATV, and RV dealerships with vehicle-specific scripts.' },
            ].map((f, i) => (
              <div key={i} style={styles.featureItem} className="ai-feature-item">
                <div style={styles.featureIconWrap}>
                  {React.cloneElement(f.icon, { size: 28, color: '#FFFFFF' })}
                </div>
                <div>
                  <h3 style={styles.featureTitle}>{f.title}</h3>
                  <p style={styles.featureDesc}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection} className="ai-section">
        <div style={styles.container}>
          <h2 style={styles.ctaH2} className="ai-cta-h2">
            Ready to Put AI to Work <span style={{ color: '#FFC300' }}>AT YOUR DEALERSHIP?</span>
          </h2>
          <p style={styles.ctaText} className="ai-cta-text">
            Every missed call is a missed deal. Let's build your AI BDC and make sure that never happens again.
          </p>
          <Link to="/contact#contact-form" style={styles.btnPrimary} className="ai-btn">
            <Mic size={22} style={{ marginRight: '10px' }} />
            Book a FREE AI BDC Demo
          </Link>
        </div>
      </section>
    </div>
  );
};

const styles = {
  page: { paddingTop: '109px' },

  container: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '0 16px',
  },

  /* Hero */
  hero: {
    position: 'relative',
    background: 'linear-gradient(135deg, #0F1118 0%, #1A1F3A 60%, #0F1118 100%)',
    padding: 'clamp(3.5rem,8vw,7rem) 0',
    textAlign: 'center',
    overflow: 'hidden',
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'radial-gradient(circle at 50% 40%, rgba(0,128,255,0.15) 0%, transparent 65%), radial-gradient(circle at 80% 80%, rgba(227,30,36,0.12) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  heroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'rgba(74, 222, 128, 0.1)',
    border: '2px solid rgba(74, 222, 128, 0.4)',
    borderRadius: '9999px',
    padding: '10px 20px',
    color: '#4ADE80',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    marginBottom: '28px',
    position: 'relative',
    zIndex: 1,
  },
  heroH1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '72px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '3px',
    position: 'relative',
    zIndex: 1,
  },
  heroSubheadline: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '22px',
    color: '#D1D5DB',
    maxWidth: '800px',
    margin: '0 auto 48px',
    lineHeight: '1.7',
    position: 'relative',
    zIndex: 1,
  },
  heroStats: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '40px',
    flexWrap: 'wrap',
    position: 'relative',
    zIndex: 1,
  },
  heroStat: { textAlign: 'center' },
  heroStatNum: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '48px',
    fontWeight: '700',
    color: '#FFC300',
    lineHeight: '1',
    marginBottom: '6px',
  },
  heroStatLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '13px',
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600',
  },
  heroStatDivider: {
    width: '2px',
    height: '60px',
    background: 'rgba(255,255,255,0.15)',
    borderRadius: '1px',
  },

  /* Section heading */
  sectionH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '52px',
    color: '#1A1F3A',
    marginBottom: '48px',
    letterSpacing: '2px',
    lineHeight: '1.1',
  },

  /* Problem/Solution */
  problemSection: {
    padding: 'clamp(3rem,6vw,6.25rem) 0',
    background: '#F5F5F5',
  },
  introGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px,100%), 1fr))',
    gap: '32px',
  },
  problemBox: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '36px 32px',
    border: '3px solid #E5E5E5',
    borderTop: '6px solid #E31E24',
  },
  problemTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#E31E24',
    marginBottom: '24px',
    letterSpacing: '1px',
  },
  problemItem: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    marginBottom: '14px',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    color: '#555',
    lineHeight: '1.5',
  },
  xIcon: {
    color: '#E31E24',
    fontWeight: '900',
    fontSize: '16px',
    flexShrink: 0,
    marginTop: '1px',
  },
  solutionBox: {
    background: '#1A1F3A',
    borderRadius: '16px',
    padding: '36px 32px',
    border: '3px solid rgba(74,222,128,0.3)',
    borderTop: '6px solid #4ADE80',
  },
  solutionTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    color: '#4ADE80',
    marginBottom: '24px',
    letterSpacing: '1px',
  },
  solutionItem: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    marginBottom: '14px',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    color: '#D1D5DB',
    lineHeight: '1.5',
  },

  /* Channels */
  channelsSection: {
    padding: 'clamp(3rem,6vw,6.25rem) 0',
    background: 'linear-gradient(135deg, #0F1118 0%, #1A1F3A 100%)',
  },
  channelsSubhead: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '18px',
    color: '#9CA3AF',
    textAlign: 'center',
    maxWidth: '700px',
    margin: '-24px auto 48px',
    lineHeight: '1.7',
  },
  channelsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px,100%), 1fr))',
    gap: '32px',
  },
  channelCard: {
    background: 'rgba(255,255,255,0.05)',
    border: '2px solid rgba(255,255,255,0.1)',
    borderRadius: '20px',
    padding: '40px 36px',
    backdropFilter: 'blur(8px)',
  },
  channelIconWrap: {
    width: '88px',
    height: '88px',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  },
  channelTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '32px',
    color: '#FFFFFF',
    marginBottom: '16px',
    letterSpacing: '1.5px',
  },
  channelDesc: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '15px',
    color: '#D1D5DB',
    lineHeight: '1.7',
    marginBottom: '28px',
  },
  channelFeatures: {},
  channelFeatureItem: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    marginBottom: '12px',
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#E5E7EB',
    lineHeight: '1.5',
  },

  /* How It Works */
  howSection: {
    padding: 'clamp(3rem,6vw,6.25rem) 0',
    background: '#F5F5F5',
  },
  stepsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px,100%), 1fr))',
    gap: '40px',
  },
  stepCard: {
    background: '#FFFFFF',
    borderRadius: '16px',
    padding: '40px 28px 28px',
    textAlign: 'center',
    border: '3px solid #E5E5E5',
    position: 'relative',
  },
  stepNumber: {
    position: 'absolute',
    top: '-22px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    color: '#FFFFFF',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(227,30,36,0.4)',
  },
  stepIconWrap: {
    marginTop: '12px',
    marginBottom: '16px',
  },
  stepTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    color: '#1A1F3A',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  stepDesc: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    lineHeight: '1.7',
    color: '#666',
  },

  /* Stats */
  statsSection: {
    padding: 'clamp(3rem,6vw,6.25rem) 0',
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px,100%), 1fr))',
    gap: '24px',
  },
  statCard: {
    background: 'rgba(255,255,255,0.15)',
    borderRadius: '16px',
    padding: '36px 28px',
    textAlign: 'center',
    backdropFilter: 'blur(8px)',
  },
  statNum: {
    fontFamily: 'Rajdhani, sans-serif',
    fontSize: '56px',
    fontWeight: '700',
    color: '#FFFFFF',
    lineHeight: '1',
    marginBottom: '12px',
  },
  statLabel: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: 'rgba(255,255,255,0.9)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '600',
    lineHeight: '1.4',
  },

  /* Features */
  featuresSection: {
    padding: 'clamp(3rem,6vw,6.25rem) 0',
    background: '#FFFFFF',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px,100%), 1fr))',
    gap: '24px',
  },
  featureItem: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    padding: '24px 20px',
    background: '#F5F5F5',
    borderRadius: '12px',
    border: '2px solid #E5E5E5',
  },
  featureIconWrap: {
    width: '52px',
    height: '52px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #1A1F3A 0%, #2C2C2C 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  featureTitle: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '22px',
    color: '#1A1F3A',
    marginBottom: '6px',
    letterSpacing: '0.5px',
  },
  featureDesc: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.6',
  },

  /* CTA */
  ctaSection: {
    background: 'linear-gradient(135deg, #1A1F3A 0%, #0F1118 100%)',
    padding: 'clamp(3rem,6vw,6.25rem) 0',
    textAlign: 'center',
  },
  ctaH2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '52px',
    color: '#FFFFFF',
    marginBottom: '24px',
    letterSpacing: '2px',
    lineHeight: '1.1',
  },
  ctaText: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '20px',
    color: '#D1D5DB',
    marginBottom: '40px',
    maxWidth: '700px',
    margin: '0 auto 40px',
    lineHeight: '1.7',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #E31E24 0%, #FF6B35 100%)',
    color: '#FFFFFF',
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px',
    padding: '20px 48px',
    borderRadius: '8px',
    textDecoration: 'none',
    boxShadow: '0 6px 24px rgba(227,30,36,0.4)',
    transition: 'all 0.3s ease',
    letterSpacing: '1px',
  },
};

export default AiBdcPage;
